"use client";
import { useState } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { DefaultValues, FormProvider } from "react-hook-form";
import { z } from "zod";
import { requiredString } from "@/lib/zod";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { FormInputField } from "@/components/form/FormInputField";
import { FormCheckboxBlock } from "@/components/form/FormCheckboxBlock";
import { useForm } from "@/hooks/useForm";
import { usePollForCard } from "../hooks/usePollForCard";

const cardFormSchema = z.object({
  cardholderName: requiredString,
  consent: z.literal(true),
});

type CardFormValues = z.infer<typeof cardFormSchema>;

const defaultValues: DefaultValues<CardFormValues> = {
  cardholderName: "",
};

interface StripeCardFormInnerProps {
  patientId: string;
  onSuccess: () => void;
}

function StripeCardFormInner({
  patientId,
  onSuccess,
}: StripeCardFormInnerProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const { form, isSubmitDisabled } = useForm<CardFormValues>(cardFormSchema, {
    defaultValues,
  });

  const pollForCard = usePollForCard(patientId);

  const handleSubmit = form.handleSubmit(async ({ cardholderName }) => {
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setStripeError(null);

    const { error } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: {
          billing_details: {
            name: cardholderName,
            address: {
              postal_code: "",
              city: "",
              country: "US",
              line1: "",
              line2: "",
              state: "",
            },
          },
        },
      },
    });

    if (error) {
      const message =
        error.message ?? "Card could not be saved. Please try again.";
      setIsSubmitting(false);
      setStripeError(message);
    } else {
      await pollForCard();
      setIsSubmitting(false);
      onSuccess();
    }
  });

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-5">
        <div className="-m-4">
          <PaymentElement
            onReady={() => setIsReady(true)}
            options={{
              terms: { card: "never" },
              wallets: {
                applePay: "never",
                googlePay: "never",
                link: "never",
              },
              fields: {
                billingDetails: {
                  address: "never",
                  name: "auto",
                },
              },
            }}
            onChange={(event) => {
              setCompleted(event.complete);
            }}
          />
        </div>
        {isReady && (
          <>
            <FormInputField
              name="cardholderName"
              label="Cardholder Name"
              placeholder="Michael Thompson"
            />
            <FormCheckboxBlock
              name="consent"
              disabled={isSubmitting}
              label="Patient consents to storing this card on file for future charges."
            />
            {stripeError && <Alert variant="error" message={stripeError} />}

            <Button
              size="lg"
              variant="primary"
              onClick={handleSubmit}
              disabled={!stripe || !completed || isSubmitDisabled}
              loading={isSubmitting}
              className="flex-1"
            >
              Save Card
            </Button>
          </>
        )}
      </div>
    </FormProvider>
  );
}

interface StripeCardFormProps {
  patientId: string;
  stripePromise: Promise<Stripe | null>;
  clientSecret: string;
  onSuccess: () => void;
}

export function StripeCardForm({
  patientId,
  stripePromise,
  clientSecret,
  onSuccess,
}: StripeCardFormProps) {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          variables: {
            fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
            labelFontSize: "0.8125rem",
            fontSizeSm: "0.75rem",
            gridRowSpacing: "18px",
            labelSpacing: "8px",
            labelColorText: "#374151",
            labelFontWeight: "600",
            borderRadius: "12px",
            inputColorBorder: "#EBEDF2",
            inputBoxShadow: "none",
            colorDanger: "#E02800",
            inputFocusColorBorder: "#111827",
            focusBoxShadow: "none",
            inputFocusBoxShadow: "none",
          },
          rules: {
            ".Input": {
              padding: "16px",
              fontSize: "13px",
            },
            ".Input--invalid": {
              boxShadow: "none",
            },
            ".AccordionItem": {
              border: "none",
              boxShadow: "none",
            },
          },
        },
      }}
    >
      <StripeCardFormInner patientId={patientId} onSuccess={onSuccess} />
    </Elements>
  );
}
